<template>
  <div v-if="snippet">
    <h1>Snippet</h1>
    <p>
      <v-btn :to="{ name: 'snippets' }">
        <v-icon>chevron_left</v-icon>Snippets
      </v-btn>
      <v-btn
        :to="{ name: 'template', params: { id: template.id } }"
        v-if="template"
      >
        <v-icon>edit</v-icon>
        {{ template.code }}
      </v-btn>
    </p>
    <p>
      <v-text-field
        label="Name"
        v-model="snippet.name"
        required="true"
        outline
      ></v-text-field>
      <v-btn @click="updateSnippet"> <v-icon>save</v-icon>Save </v-btn>
    </p>
    <h3>Revisions</h3>
    <p>
      <v-btn @click="createRevision">
        <v-icon>add</v-icon>Create revision
      </v-btn>
    </p>
    <div v-if="templateRevisions">
      <v-data-table
        :headers="headers"
        :items="modifiedTemplateRevisions"
        hide-actions
        must-sort
        class="elevation-1"
      >
        <template v-slot:items="props">
          <tr>
            <td>
              <v-btn
                :to="{
                  name: 'revision',
                  params: { id: props.item.id, raw: false }
                }"
                small
              >
                <v-icon>edit</v-icon>
                &#160;{{ props.item.id }}
              </v-btn>
            </td>
            <td>{{ props.item.timestamp | formatDate }}</td>
            <td>
              <EntityZ v-if="props.item.user" :entity="props.item.user" />
              <span v-if="!props.item.user">{{ props.item.userid }}</span>
            </td>
            <td>
              <v-btn
                :to="{ name: 'revisionCopy', params: { id: props.item.id } }"
                small
              >
                <v-icon>file_copy</v-icon>&#160;Copy
              </v-btn>
            </td>
            <td>
              <span v-if="props.item.locked" class="revisionEditable"
                >Editable</span
              >
              <span v-if="!props.item.locked" class="revisionPublished"
                >Published</span
              >
            </td>
            <td>
              <v-btn @click="download(props.item)" small>
                <v-icon>cloud_download</v-icon>&#160;Download
              </v-btn>
            </td>
          </tr>
        </template>
      </v-data-table>
      <h3>Import template revision</h3>
      <v-form ref="uploadForm" v-model="valid" lazy-validation>
        <v-input :rules="fileRules" prepend-icon="attach_file">
          <UploadButton ripple title="Choose file" @file-update="setFile">
            <template slot="icon-left">
              <v-icon>folder</v-icon>
            </template>
          </UploadButton>
        </v-input>
        <v-btn @click="upload"> <v-icon>save</v-icon>&#160;Upload </v-btn>
      </v-form>
    </div>
  </div>
</template>

<script>
import UploadButton from "vuetify-upload-button";
import EntityZ from "@/components/link/EntityZ";
import { vectorNotBlank, convertListOfObjectsToMap } from "@/util/helpers";
import nbmlaw from "@/api/nbmlaw";
export default {
  name: "Snippet",
  metaInfo() {
    return {
      title: "Snippet",
      titleTemplate: "%s - Document - Conveyancing Tracker",
      htmlAttrs: {
        lang: "en",
        amp: true
      }
    };
  },
  components: { EntityZ, UploadButton },
  computed: {
    modifiedTemplateRevisions() {
      if (!this.templateRevisions) {
        return undefined;
      }
      const entitiesMap = this.entities
        ? convertListOfObjectsToMap(this.entities, "id")
        : undefined;
      return this.templateRevisions.map((t) => {
        const user =
          entitiesMap && entitiesMap[t.userid]
            ? entitiesMap[t.userid]
            : undefined;
        return { ...t, user };
      });
    }
  },
  data() {
    return {
      snippet: null,
      template: null,
      templateRevisions: null,
      entities: null,
      raw: false,
      fileData: null,
      valid: false,
      fileRules: [() => !!this.fileData || "File is required"],
      headers: [
        {
          text: "Revision id",
          value: "id"
        },
        {
          text: "Timestamp",
          value: "timestamp"
        },
        {
          text: "Creator",
          value: "valueOrder"
        },
        {
          text: " ",
          sortable: false
        },
        {
          text: " ",
          sortable: false
        },
        {
          text: " ",
          sortable: false
        }
      ]
    };
  },
  methods: {
    async fetchSnippet() {
      const response = await nbmlaw.get(
        `/cors/admin/document/snippets/${this.$route.params.id}`
      );
      // console.log(response.data);
      this.snippet = response.data;
      this.fetchTemplate();
    },
    async fetchTemplate() {
      const response = await nbmlaw.get(
        `/cors/admin/document/templates/${this.snippet.templateid}`
      );
      // console.log(response.data);
      this.template = response.data;
    },
    async fetchTemplateRevisions() {
      const response = await nbmlaw.get(
        `/cors/admin/document/snippets/${this.$route.params.id}/templateRevisions`
      );
      // console.log(response.data);
      this.templateRevisions = response.data;
      this.fetchEntities();
    },
    async fetchEntities() {
      const ids = [
        ...new Set(vectorNotBlank(this.templateRevisions, "userid"))
      ];
      // console.log("User length: " + ids);
      if (ids.length > 0) {
        const ids_ = ids.join(".");
        const response = await nbmlaw.get(
          `/cors/admin/directory/entities/multiple/${ids_}`
        );
        // console.log(response.data);
        this.entities = response.data;
      }
    },
    updateSnippet() {},
    async createRevision() {
      await nbmlaw.post(
        `/cors/admin/document/snippets/${this.$route.params.id}/templateRevisions`,
        null
      );
      this.fetchTemplateRevisions();
    },
    download(templateRevision) {
      window.open(
        `${process.env.VUE_APP_CT_URL}/cors/admin/document/templateRevisions/${templateRevision.id}/pdf`,
        "_blank"
      );
    },
    setFile(file) {
      // console.log(file);
      this.fileData = file;
    },
    async upload() {
      if (this.$refs.uploadForm.validate()) {
        let formData = new FormData();
        formData.append("file", this.fileData);
        try {
          await nbmlaw.post(
            `/cors/admin/document/snippets/${this.$route.params.id}/templateRevisions/xml`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data"
              }
            }
          );
          this.fetchTemplateRevisions();
          this.fileData = null;
        } catch (err) {
          console.error(err);
        }
      }
    },
    update() {
      this.fetchSnippet();
      this.fetchTemplateRevisions();
    }
  },
  watch: {
    "$route.params.id": {
      immediate: true,
      handler() {
        if (this.$route.params.id) {
          this.update();
        } else {
          this.snippet = { name: "NEW NAME" };
        }
      }
    }
  }
};
</script>
